/**
 * The unread "New" badge (#1069 P3.3, FR54).
 *
 * ⚠️ IT HAS EXACTLY ONE CALL SITE, AND THAT IS THE DESIGN: the **space tabs**
 * in `CorrespondencePanel`. The thread card, the request-list row and the
 * launcher pill all keep a plain `UnreadDot` — the owner's explicit call,
 * verbatim **"Card keeps plain dot"** (2026-08-25), after he had asked for a
 * badge on the tabs in the same breath. Extending this badge to any of those
 * three to "finish the job" would be undoing a decision, not completing one.
 *
 * ⚠️ IT IS `UnreadDot` WITH A WORD IN IT, AND IT KEEPS EVERY ONE OF THAT
 * COMPONENT'S RULES. Read `UnreadDot.tsx` first; only the differences are
 * restated here.
 *
 * ⚠️ THE SIGNAL IS PRESENCE, NEVER HUE — and now never a WORD that changes
 * either. It renders NOTHING when there is nothing unread: no wrapper, no
 * greyed "New", no zero-opacity spacer, no "Read" counterpart. A badge that is
 * always present and merely recoloured fails WCAG 1.4.1 exactly as a
 * two-tone dot does, and would pass a screenshot review.
 *
 * ⚠️ THE WORD STRENGTHENS THE FR55 POSITION RATHER THAN WEAKENING IT. FR55
 * requires the signal be carried by more than colour alone; a shape carries it,
 * and a word carries it further. The tab is the one surface with room for it:
 * a text badge is impractical in a dense request-list cell and on a small round
 * pill, and it would re-create on the thread card exactly the top-right
 * "something numeric lives here" slot that PRD r8 emptied.
 *
 * ⚠️ THE GROUND IS `colors.unreadBadge`, NOT `colors.error`, AND THE REASON IS
 * MEASURED. `#fff` on `colors.error` (`#FF3B30`) is **3.55:1**, which fails
 * SC 1.4.3's 4.5:1 for text this size; so does `#333` on it (3.56:1), and so
 * does inverting to red-on-white (3.55:1). `#fff` on `colors.unreadBadge`
 * (`#D93228`) is **4.73:1** and passes. As a SHAPE (SC 1.4.11, 3:1) the badge
 * is **4.73:1 on a white card and on EITHER tab, selected or not**.
 *
 * ⚠️ THE TAB CLAIM IN THIS PARAGRAPH WAS RETIRED BY #1069 STORY 7.1
 * (2026-09-18). It used to read "3.87:1 on a selected tab, which since
 * 2026-09-08 carries a `surfaceMessage` fill … that 3.87 is the badge's
 * floor". Story 7.1 DELETED that fill: selection is now a `primaryStrong`
 * underline and a weight step, so on a tab the badge's ground is white in
 * both states and the number went UP, 3.87 → 4.73.
 *
 * ⚠️ THAT DOES NOT MEAN THE BADGE NEVER SITS ON BLUE. `surfaceMessage` is
 * still the ground of message bubbles and of the unread thread avatar, so
 * 3.87:1 is still the badge's floor and `src/theme/__tests__/colors.test.ts`
 * still pins it there — computed through `src/theme/contrast.ts` rather than
 * trusted from this paragraph. What changed is WHERE the floor comes from,
 * not what it is.
 *
 * ⚠️ NO LABEL OF ITS OWN, AND THE VISIBLE WORD IS HIDDEN FROM THE
 * ACCESSIBILITY TREE. The tab already speaks its own state by extending its
 * `accessibilityLabel`; letting this `Text` through as well would announce
 * "New" twice on the same control.
 *
 * ⚠️ THE VOCABULARY RULE, AND IT IS NARROWER THAN "ONE WORD EVERYWHERE".
 * The non-negotiable part is that a single element must never SHOW one word
 * and SAY another. The tab is the only element in this feature with a visible
 * word, so the tab shows "New" and says "New". Every dot surface has no
 * visible word to contradict, so it keeps the more precise state term and is
 * announced as "unread". The alternative — "New" everywhere — would give
 * VoiceOver "Open discussion, new" on the launcher pill and lead every unread
 * thread card with a vaguer word than the one it replaced, for no gain to
 * anybody, since the badge is not on those surfaces to disagree with.
 *
 * ⚠️ NO ANIMATION, NO PULSE, NO TRANSITION. Same reason as the dot: the PRD
 * makes asynchronicity a requirement, nothing here polls, and a pulsing badge
 * would be a lie about urgency plus a reduced-motion obligation invented from
 * nothing.
 *
 * @see src/components/correspondence/UnreadDot.tsx — the rule, and the surfaces that keep it
 * @see GitLab #1069 — Request Correspondence
 */

import React from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { colors } from '../../theme/colors';

import type { StyleProp, ViewStyle } from 'react-native';

/**
 * The word, exported so a test cannot drift from the render and so the two
 * surfaces that also SPEAK it compose the same string.
 */
export const UNREAD_BADGE_WORD = 'New';

interface Props {
  /**
   * `undefined` means the server's read-state lookup failed for this listing
   * (since Story 2.1 that is its only meaning: an oversight reader's unread is
   * computed like everybody else's, FR52), and `false` means it computed and
   * found nothing. Both render as nothing, which is why the distinction is kept
   * in the types rather than here.
   */
  unread?: boolean;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

export const UnreadBadge: React.FC<Props> = ({
  unread,
  style,
  testID = 'unread-badge',
}) =>
  unread ? (
    <View
      style={[styles.badge, style]}
      testID={testID}
      /**
       * ⚠️ IT MUST NEVER EAT A TAP. On the thread card the badge sits over a
       * `TouchableOpacity` that IS the whole row; without this, the ~34px it
       * occupies would swallow presses in the corner the eye is drawn to and
       * the card would read as dead exactly there. It is a status, never a
       * target, at every level.
       */
      pointerEvents="none"
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
    >
      <Text style={styles.text}>{UNREAD_BADGE_WORD}</Text>
    </View>
  ) : null;

const styles = StyleSheet.create({
  badge: {
    backgroundColor: colors.unreadBadge,
    borderRadius: 9,
    paddingHorizontal: 6,
    paddingVertical: 1,
    // Never let the word wrap or the pill stretch to fill a flex row.
    alignSelf: 'flex-start',
    flexShrink: 0,
  },
  text: {
    color: colors.textLight,
    fontSize: 10,
    lineHeight: 14,
    fontWeight: '700',
  },
});
